import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, inject, Input, OnDestroy, Output, signal, ViewChild } from '@angular/core';
import { BreakpointObserverService } from 'src/app/shared/services/breakpoint-observer/breakpoint-observer.service';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { QuillEditorComponent } from 'ngx-quill';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-quill-toolbar',
  standalone: true,
  imports: [MatIconModule, MatButtonModule, MatTooltipModule, TranslateModule, MatProgressSpinnerModule],
  templateUrl: './quill-toolbar.component.html',
  styleUrl: './quill-toolbar.component.scss',
})
export class QuillToolbarComponent implements AfterViewInit, OnDestroy {
  private elementRef = inject(ElementRef);
  public breakpointService = inject(BreakpointObserverService);
  private _editor: QuillEditorComponent;

  @ViewChild('toolbarContainer') toolbarContainer!: ElementRef<HTMLDivElement>;

  showScrollButtons = signal(false);
  private resizeObserver: ResizeObserver | null = null;

  @Input() showChunkOptions = false;
  @Input() canUndo = false;
  @Input() canRedo = false;

  @Output() undo = new EventEmitter<void>();
  @Output() redo = new EventEmitter<void>();
  @Output() copy = new EventEmitter<void>();
  @Output() convertToInlineFormula = new EventEmitter<void>();
  @Output() insertLink = new EventEmitter<void>();

  @Input()
  set editor(editor: QuillEditorComponent) {
    if (editor && editor !== this._editor) {
      this._editor = editor;
      this.selectionChangeSubscription?.unsubscribe();
      this.selectionChangeSubscription = this._editor.onSelectionChanged.subscribe((event: any) => {
        if (!event || !event.range) {
          return;
        }
        const format = this._editor.quillEditor.getFormat(event.range);
        this.currentColor = format.color || 'inherit';
        this.currentBackground = format.background || 'inherit';
        const headerValue = format.header;
        this.currentHeaderIcon = headerValue ? `format_h${headerValue}` : 'text_fields';
      });
    }
  }
  get editor(): QuillEditorComponent {
    return this._editor;
  }

  currentHeaderIcon = 'text_fields';
  currentSizeIcon = 'text_format';
  currentFontIcon = 'font_download';
  currentAlignIcon = 'format_align_left';
  currentColor: unknown = 'inherit';
  currentBackground: unknown = 'inherit';

  activePanel = signal<string | null>(null);

  readonly colorPalette: string[] = [
    '#000000',
    '#e60000',
    '#ff9900',
    '#ffff00',
    '#008a00',
    '#0066cc',
    '#9933ff',
    '#ffffff',
    '#facccc',
    '#ffebcc',
    '#ffffcc',
    '#cce8cc',
    '#cce0f5',
    '#ebd6ff',
    '#bbbbbb',
    '#f06666',
    '#ffc266',
    '#ffff66',
    '#66b966',
    '#66a3e0',
    '#c285ff',
    '#888888',
    '#a10000',
    '#b26b00',
    '#b2b200',
    '#006100',
    '#0047b2',
    '#6b24b2',
    '#444444',
    '#5c0000',
    '#784700',
    '#787800',
    '#003d00',
    '#002978',
    '#471778',
    '#111111',
    '#2d0000',
    '#4d2d00',
    '#4d4d00',
    '#001f00',
    '#00174d',
    '#290e4d',
  ];

  readonly formatSizeItems = [
    { id: '12px' as string | false, text: '12px', icon: 'format_size' },
    { id: '14px' as string | false, text: '14px', icon: 'format_size' },
    { id: '16px' as string | false, text: '16px', icon: 'format_size' },
    { id: '18px' as string | false, text: '18px', icon: 'format_size' },
    { id: '20px' as string | false, text: '20px', icon: 'format_size' },
    { id: '22px' as string | false, text: '22px', icon: 'format_size' },
    { id: false as string | false, text: 'Quill.Size.Normal', icon: 'format_size' },
  ];

  readonly formatHeaderItems = [
    { id: 1 as number | false, text: 'Quill.Header.1', icon: 'format_h1' },
    { id: 2 as number | false, text: 'Quill.Header.2', icon: 'format_h2' },
    { id: 3 as number | false, text: 'Quill.Header.3', icon: 'format_h3' },
    { id: 4 as number | false, text: 'Quill.Header.4', icon: 'format_h4' },
    { id: 5 as number | false, text: 'Quill.Header.5', icon: 'format_h5' },
    { id: 6 as number | false, text: 'Quill.Header.6', icon: 'format_h6' },
    { id: false as number | false, text: 'Quill.Header.Normal', icon: 'text_fields' },
  ];

  readonly formatFontItems = [
    { id: 'arial' as string | false, text: 'Arial', icon: 'font_download' },
    { id: 'courier-new' as string | false, text: 'Courier New', icon: 'font_download' },
    { id: 'georgia' as string | false, text: 'Georgia', icon: 'font_download' },
    { id: 'helvetica' as string | false, text: 'Helvetica', icon: 'font_download' },
    { id: 'lucida-sans' as string | false, text: 'Lucida Sans', icon: 'font_download' },
    { id: 'tahoma' as string | false, text: 'Tahoma', icon: 'font_download' },
    { id: 'times-new-roman' as string | false, text: 'Times New Roman', icon: 'font_download' },
    { id: 'trebuchet-ms' as string | false, text: 'Trebuchet MS', icon: 'font_download' },
    { id: 'verdana' as string | false, text: 'Verdana', icon: 'font_download' },
    { id: false as string | false, text: 'Quill.Font.Default', icon: 'font_download' },
  ];

  readonly alignmentItems = [
    { id: false as string | false, icon: 'format_align_left', tooltipKey: 'Quill.Tooltip.Align.Left' },
    { id: 'center' as string | false, icon: 'format_align_center', tooltipKey: 'Quill.Tooltip.Align.Center' },
    { id: 'right' as string | false, icon: 'format_align_right', tooltipKey: 'Quill.Tooltip.Align.Right' },
    { id: 'justify' as string | false, icon: 'format_align_justify', tooltipKey: 'Quill.Tooltip.Align.Justify' },
  ];

  private selectionChangeSubscription: Subscription;

  @HostListener('document:mousedown', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.activePanel() && !this.elementRef.nativeElement.contains(event.target)) {
      this.activePanel.set(null);
    }
  }

  scrollToolbar(direction: 'left' | 'right'): void {
    if (this.toolbarContainer) {
      const scrollAmount = 150;
      this.toolbarContainer.nativeElement.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  }

  ngAfterViewInit() {
    if (typeof window !== 'undefined' && 'ResizeObserver' in window && this.toolbarContainer) {
      this.resizeObserver = new ResizeObserver(() => {
        this.checkOverflow();
      });
      this.resizeObserver.observe(this.toolbarContainer.nativeElement);
    }
  }

  checkOverflow(): void {
    if (this.toolbarContainer) {
      const el = this.toolbarContainer.nativeElement;
      const overflow = el.scrollWidth > el.clientWidth;
      this.showScrollButtons.set(overflow);
    }
  }

  ngOnDestroy() {
    this.selectionChangeSubscription?.unsubscribe();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  togglePanel(panel: string): void {
    if (this.activePanel() === panel) {
      this.activePanel.set(null);
      return;
    }

    this.activePanel.set(panel);
  }

  applyFormat(type: string, value: string | number | boolean): void {
    this.editor.quillEditor.format(type, value, 'user');
    this.activePanel.set(null);
  }
}
